import React, {useCallback, useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {CopilotConversationDto, CreateCopilotConversationBody, UpdateCopilotConversationBody} from '../../services/CopilotConversation/type'
import {CopilotMessageDto, CreateCopilotMessageBody, SenderType} from '../../services/CopilotMessage/type'
import {CopilotConversationService} from '../../services/CopilotConversation/CopilotConversation'
import {CopilotMessageService} from '../../services/CopilotMessage/CopilotMessage'
import {WebSocketService} from '../../services/WebSocketService'
import {getCookie, getCookieName} from '../../common/utils/cookieUtils'

interface CopilotContext {
  conversations: CopilotConversationDto[]
  messages: {[conversationId: string]: CopilotMessageDto[]}
  streamingResponses: {[conversationId: string]: string}
  selectedConversation?: CopilotConversationDto
  isLoading: boolean
  streamingConversations: {[conversationId: string]: boolean}
  fetchConversationById: (id: string) => Promise<CopilotConversationDto>
  fetchConversations: () => Promise<void>
  fetchMessages: (conversationId: string) => Promise<void>
  createConversation: (title?: string) => Promise<CopilotConversationDto | null>
  sendMessage: (conversationId: string, content: any) => Promise<void>
  setSelectedConversation: (conversation?: CopilotConversationDto) => void
  deleteConversation: (conversationId: string) => Promise<void>
  updateConversation: (conversationId: string, title: string) => Promise<void>
  isConversationStreaming: (conversationId: string) => boolean
}

const Context = React.createContext<CopilotContext>(null as any)

interface CopilotContextProviderProps {
  children: React.ReactNode
}

export const CopilotContextProvider: React.FC<CopilotContextProviderProps> = ({children}) => {
  const [conversations, setConversations] = useState<CopilotConversationDto[]>([])
  const [messages, setMessages] = useState<{[conversationId: string]: CopilotMessageDto[]}>({})
  const [streamingResponses, setStreamingResponses] = useState<{[conversationId: string]: string}>({})
  const [selectedConversation, setSelectedConversation] = useState<CopilotConversationDto | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [streamingConversations, setStreamingConversations] = useState<{[conversationId: string]: boolean}>({})

  const initializeWebSocket = useCallback(async () => {
    try {
      await WebSocketService.connect()
    } catch (error) {
      console.error('Error connecting to WebSocket:', error)
    }
  }, [])

  const isConversationStreaming = useCallback(
    (conversationId: string) => {
      return streamingConversations[conversationId]
    },
    [streamingConversations]
  )

  const handleConnectionStatus = useCallback((status: 'connected' | 'disconnected') => {
    if (status === 'disconnected') {
      setStreamingConversations({})
    }
  }, [])

  useEffect(() => {
    initializeWebSocket()
    WebSocketService.addConnectionListener(handleConnectionStatus)

    return () => {
      WebSocketService.removeConnectionListener(handleConnectionStatus)
      WebSocketService.disconnect()
    }
  }, [initializeWebSocket, handleConnectionStatus])

  const setupStreamHandler = useCallback(
    (conversationId: string) => {
      if (WebSocketService.isConnected()) {
        const message = JSON.stringify({
          type: 'register',
          conversationId: conversationId,
        })
        WebSocketService.sendMessage(message)

        const hasStreamingContent = !!streamingResponses[conversationId]
        if (hasStreamingContent) {
          // setStreamingConversations((prev) => ({
          //   ...prev,
          //   [conversationId]: true,
          // }))
        }
      }

      WebSocketService.registerConversationHandler(`${conversationId}`, (data) => {
        console.log('Received data:', data)
        switch (data.type) {
          case 'chunk':
            setStreamingResponses((prev) => ({
              ...prev,
              [conversationId]: data.accumulated || (prev[conversationId] || '') + (data.content || ''),
            }))
            break
          case 'complete':
            setStreamingConversations((prev) => ({
              ...prev,
              [conversationId]: false,
            }))

            if (data.message) {
              setMessages((prev) => ({
                ...prev,
                [conversationId]: [...(prev[conversationId] || []), data.message],
              }))
            } else {
              const streamedContent = streamingResponses[conversationId] || ''
              if (streamedContent) {
                const aiMessage: CopilotMessageDto = {
                  id: Date.now(),
                  createdAt: new Date().toISOString(),
                  content: {text: streamedContent},
                  senderType: SenderType.BOT,
                }

                setMessages((prev) => ({
                  ...prev,
                  [conversationId]: [...(prev[conversationId] || []), aiMessage],
                }))
              }
            }
            cleanupStreamHandler(conversationId)

            setStreamingResponses((prev) => {
              const newState = {...prev}
              delete newState[conversationId]
              return newState
            })
            break
          case 'error':
            console.error('Streaming error:', data.error)
            setStreamingConversations((prev) => ({
              ...prev,
              [conversationId]: false,
            }))
            break
          case 'function_call':
            console.log('Function call:', data.function, data.arguments, data.result)
            break
        }
      })
    },
    [streamingResponses]
  )

  const cleanupStreamHandler = useCallback((conversationId: string) => {
    WebSocketService.unregisterConversationHandler(conversationId)
  }, [])

  useEffect(() => {
    if (selectedConversation) setupStreamHandler(selectedConversation.id)
    return () => {
      if (selectedConversation) cleanupStreamHandler(selectedConversation.id)
    }
  }, [selectedConversation, setupStreamHandler, cleanupStreamHandler])

  const fetchConversations = useCallback(async () => {
    setIsLoading(true)
    try {
      const conversations = await CopilotConversationService.getAll()
      setConversations(conversations)
    } catch (error) {
      console.error('Error fetching conversations:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchConversationById = useCallback(async (id: string) => {
    const conversation = await CopilotConversationService.getById(id)
    return conversation
  }, [])

  const fetchMessages = useCallback(async (conversationId: string) => {
    setIsLoading(true)
    try {
      const messages = await CopilotMessageService.getAll(conversationId)
      setMessages((prev) => ({
        ...prev,
        [conversationId]: messages,
      }))
    } catch (error) {
      console.error('Error fetching messages:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createConversation = async (title: string = 'New Conversation'): Promise<CopilotConversationDto | null> => {
    setIsLoading(true)
    try {
      const body: CreateCopilotConversationBody = {title}
      const newConversation = await CopilotConversationService.create(body)
      setConversations((prev) => [newConversation, ...prev])
      return newConversation
    } catch (error) {
      console.error('Error creating conversation:', error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateConversation = async (conversationId: string, title: string) => {
    try {
      const body: UpdateCopilotConversationBody = {title}
      await CopilotConversationService.update(body, conversationId)
      setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? {...conv, title} : conv)))

      if (selectedConversation?.id === conversationId) {
        setSelectedConversation((prev) => (prev ? {...prev, title} : undefined))
      }
    } catch (error) {
      console.error('Error updating conversation:', error)
    }
  }

  const deleteConversation = async (conversationId: string) => {
    try {
      await CopilotConversationService.destroy(conversationId)
      setConversations((prev) => prev.filter((conv) => conv.id !== conversationId))

      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(undefined)
      }
    } catch (error) {
      console.error('Error deleting conversation:', error)
    }
  }

  const sendMessage = async (conversationId: string, content: any) => {
    if (!content) return

    if (!WebSocketService.isConnected()) {
      await WebSocketService.connect()
    }

    const tempMessage: CopilotMessageDto = {
      id: Date.now(),
      createdAt: new Date().toISOString(),
      content: {text: content},
      senderType: SenderType.HUMAN,
    }

    setStreamingResponses((prev) => {
      const newState = {...prev}
      delete newState[conversationId]
      return newState
    })

    setMessages((prev) => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), tempMessage],
    }))

    try {
      setStreamingConversations((prev) => ({
        ...prev,
        [conversationId]: true,
      }))

      const token = getCookie(getCookieName())

      const message = JSON.stringify({
        type: 'create_message',
        conversationId: conversationId,
        content: {text: content},
        senderType: SenderType.HUMAN,
        token: token,
      })

      WebSocketService.sendMessage(message)
    } catch (error) {
      console.error('Error sending message:', error)
      setStreamingConversations((prev) => ({
        ...prev,
        [conversationId]: false,
      }))
    }
  }

  return (
    <Context.Provider
      value={{
        conversations,
        messages,
        streamingResponses,
        selectedConversation,
        isLoading,
        streamingConversations,
        isConversationStreaming,
        fetchConversationById,
        fetchConversations,
        fetchMessages,
        createConversation,
        sendMessage,
        setSelectedConversation,
        deleteConversation,
        updateConversation,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useCopilotContext = () => useContext(Context)
